/**
 * Conviction ("TWAR") — projection of the on-chain conviction line. EXACT
 * bigint port of the authoritative contract math in
 * `contracts/markets/programs/markets/src/instructions/trading/conviction.rs`.
 * No floats anywhere (see `.claude/rules/numeric-precision.md`); same
 * never-through-`number` discipline as the rest of this package's math.
 *
 * The fixed-point recurrence primitives live in `@morfi/market-math`; this
 * module owns the cached-row projection adapter used by backend-sdk and API
 * consumers. Keeping the recurrence in market-math also makes executable
 * pre-trade quote gates and display projection share one implementation.
 *
 * The chain computes conviction and emits the authoritative POST-trade state
 * in every `TradeEvent` (`post_outcome_conviction_ppb` /
 * `post_outcome_conviction_target_ppb` /
 * `post_conviction_retention_per_second_ppb` /
 * `post_last_conviction_update_ts`). We NEVER re-derive conviction from
 * reserve shares / momentum (that would risk diverging from chain) — we
 * snapshot the authoritative values and only INTERPOLATE between trades.
 *
 * Between trades the contract holds each outcome's `target` constant (target
 * is only recomputed from reserves on a trade); the only thing that changes
 * is the elapsed-time relaxation toward that frozen target. So projecting
 * with the stored `target` + `retention^elapsed` is exactly what the chain
 * will compute for the time-decay component at the next trade —
 * `projectRowConviction` replicates `blend_conviction_ppb ∘ fixed_pow_ppb`
 * bit-for-bit.
 */

import {
  blendConvictionPpb,
  CONVICTION_SCALE_PPB,
  fixedMulPpb,
  fixedPowPpb,
} from '@morfi/market-math';

export { blendConvictionPpb, CONVICTION_SCALE_PPB, fixedMulPpb, fixedPowPpb };
/**
 * Documented DEFAULT per-second conviction retention (PPB) — the
 * protocol's reference calibration: `998_950_328` ≈ an 11-minute (660 s)
 * half-life (opinion-market-protocol.html §2 / "Calibration cookbook").
 *
 * NOT a platform-wide constant: the protocol explicitly supports multiple
 * coexisting `MarketConfig`s with different half-lives (`config-fast`
 * ~5 min, `config-slow` ~30 min, …). The authoritative per-market value
 * travels with the conviction snapshot — `convictionRetentionPpb` on the
 * row (REST: `market_conviction_points.retention_per_second_ppb`; live:
 * the trade event's `postConvictionRetentionPerSecondPpb`). The projector
 * uses the per-row value and falls back to THIS constant only when the
 * row carries none (pre-first-trade / older payloads), so the
 * between-trade curve matches the chain for whichever config the market
 * was created under.
 */
export const CONVICTION_RETENTION_PER_SECOND_PPB = 998_950_328n;

/** Coerce a wire value (proto uint32 → number, int64 → bigint, API → string)
 *  to a non-negative bigint. Invalid / nullish → 0n. */
export function toPpbBigint(v: string | number | bigint | null | undefined): bigint {
  if (typeof v === 'bigint') return v >= 0n ? v : 0n;
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return 0n;
    const t = Math.trunc(v);
    return t > 0 ? BigInt(t) : 0n;
  }
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (trimmed === '' || !/^\d+$/.test(trimmed)) return 0n;
    try {
      return BigInt(trimmed);
    } catch {
      return 0n;
    }
  }
  return 0n;
}

/** The conviction slice of one outcome on a cached market row — the only
 *  fields the projector reads/writes (all other outcome fields, e.g.
 *  label/imageUrl, are preserved untouched). */
export interface ProjectableOutcome {
  /** Displayed value the frontend renders — the projector writes this. */
  readonly conviction?: string;
  /** Authoritative post-trade snapshot — immutable projection input;
   *  the projector NEVER recomputes from `conviction` (no compounding). */
  readonly convictionAnchor?: string;
  /** Target the line relaxes toward — immutable projection input. */
  readonly convictionTarget?: string;
}

/** The conviction slice of a cached market row the projector advances. */
export interface ProjectableRow {
  /** Unix-seconds of the chain's last conviction update (anchor). */
  readonly convictionUpdatedAt?: string;
  /** Market lifecycle status. `'resolved'` / `'graduated'` are terminal —
   *  see {@link isTerminalMarket}. Absent ⇒ treated as live. */
  readonly status?: string | undefined;
  /** Resolution timestamp (ISO). Non-null ⇒ the market has resolved (stays
   *  set through graduation) ⇒ conviction is frozen on-chain. */
  readonly resolvedAt?: string | null | undefined;
  readonly outcomes?: ReadonlyArray<ProjectableOutcome>;
  readonly outcomesLite?: ReadonlyArray<ProjectableOutcome>;
}

/**
 * A market whose on-chain conviction is frozen. Resolution is
 * trade-triggered (the resolving trade IS the last conviction update), so
 * after resolution the chain never advances conviction again — the cached
 * `conviction` already holds the authoritative final value. Projecting such
 * a row past `resolvedAt` relaxes it toward a now-meaningless target and
 * drifts the displayed value away from chain truth (e.g. a winner reading
 * 127% at resolution creeps to 175% an hour later). `graduated` is terminal
 * too (a market graduates only after resolving). Either signal — when the
 * surface requested it — freezes the row.
 */
export function isTerminalMarket(row: ProjectableRow): boolean {
  if (row.status === 'resolved' || row.status === 'graduated') return true;
  const resolvedAt = row.resolvedAt;
  return typeof resolvedAt === 'string' && resolvedAt.trim() !== '';
}

/**
 * Advance a cached market row's per-outcome `conviction` to wall-clock
 * `nowSecs` by relaxing each outcome's authoritative `convictionAnchor`
 * toward `convictionTarget` over the time elapsed since
 * `convictionUpdatedAt` — the exact `blend_conviction_ppb ∘
 * fixed_pow_ppb` recurrence using the market's per-config retention
 * passed explicitly by the catalog owner. This is what
 * the SDK's internal projector ticks so the frontend only ever renders
 * `outcomes[i].conviction` (no events needed between trades — see the
 * "10k trade then a quiet hour" case).
 *
 * Immutable + structural-sharing: returns the SAME `row` reference when
 * nothing moved, and reuses unchanged outcome element references — so
 * TanStack field subscriptions only re-render rows that actually
 * changed. `convictionAnchor` / `convictionTarget` are never mutated
 * (projection always extrapolates from the anchor). At `elapsed = 0`
 * (REST seed: `conviction == convictionAnchor`) the blend returns the
 * anchor unchanged → same ref, zero churn until time passes.
 *
 * TOTAL — every non-projectable input (null/undefined, a non-object, or a
 * row without a populated `outcomes` array — e.g. a watchlist row or a
 * `null` hole in a paged list) returns the SAME reference. The projector
 * relies on this to safely scan EVERY observed query without per-entity
 * type checks; a throw here would crash the projector's setInterval tick.
 */
export function projectRowConviction<T extends ProjectableRow>(
  row: T,
  nowSecs: bigint,
  retentionPerSecond: bigint,
): T {
  if (row === null || typeof row !== 'object') return row;
  const outcomes = row.outcomes;
  const outcomesLite = row.outcomesLite;
  const hasOutcomes = Array.isArray(outcomes) && outcomes.length > 0;
  const hasOutcomesLite = Array.isArray(outcomesLite) && outcomesLite.length > 0;
  if (!hasOutcomes && !hasOutcomesLite) return row;
  // Resolved/graduated markets freeze conviction at resolution — see
  // isTerminalMarket. Drifting them is the bug behind a winner's
  // chart legend reading 175% when the chain (and chart line) says 127%.
  if (isTerminalMarket(row)) return row;
  const updatedAt = toPpbBigint(row.convictionUpdatedAt);
  const elapsed = nowSecs > updatedAt ? nowSecs - updatedAt : 0n;
  const retention = fixedPowPpb(retentionPerSecond, elapsed);
  const projectOutcomes = (
    values: ReadonlyArray<ProjectableOutcome> | undefined,
  ): ReadonlyArray<ProjectableOutcome> | undefined => {
    if (values === undefined) return undefined;
    let changed = false;
    const projectedOutcomes = values.map((outcome) => {
      if (!isProjectableOutcome(outcome)) return outcome;
      const projected = blendConvictionPpb(
        toPpbBigint(outcome.convictionAnchor),
        toPpbBigint(outcome.convictionTarget),
        retention,
      ).toString();
      if (projected === outcome.conviction) return outcome;
      changed = true;
      return { ...outcome, conviction: projected };
    });
    return changed ? projectedOutcomes : values;
  };

  const nextOutcomes = hasOutcomes ? projectOutcomes(outcomes) : outcomes;
  const nextOutcomesLite = hasOutcomesLite ? projectOutcomes(outcomesLite) : outcomesLite;
  if (nextOutcomes === outcomes && nextOutcomesLite === outcomesLite) return row;

  return {
    ...row,
    ...(nextOutcomes !== outcomes ? { outcomes: nextOutcomes } : {}),
    ...(nextOutcomesLite !== outcomesLite ? { outcomesLite: nextOutcomesLite } : {}),
  } as T;
}

function isProjectableOutcome(outcome: ProjectableOutcome): boolean {
  return (
    outcome !== null &&
    typeof outcome === 'object' &&
    isUnsignedIntegerString(outcome.convictionAnchor) &&
    isUnsignedIntegerString(outcome.convictionTarget)
  );
}

function isUnsignedIntegerString(value: string | undefined): boolean {
  return typeof value === 'string' && /^\d+$/.test(value.trim());
}

/** Minimal vector snapshot used by non-UI consumers that need projected
 * conviction without building a `ProjectableRow` object. */
export interface ConvictionVectorSnapshot {
  readonly anchorPpb: readonly bigint[];
  readonly targetPpb: readonly bigint[];
  readonly retentionPpb: bigint;
}

export interface NormalizedLeader {
  readonly outcomeIndex: number;
  readonly leaderPpb: bigint;
  readonly totalPpb: bigint;
}

export interface NormalizedResolutionProjection {
  readonly outcomeIndex: number;
  readonly resolutionTsSecs: bigint;
  readonly normalizedScoreD9: bigint;
}

/** Project every outcome from its anchor toward its frozen target after
 * `elapsedSecs`, using the exact contract blend recurrence. */
export function projectConvictionVectorPpb(
  snapshot: ConvictionVectorSnapshot,
  elapsedSecs: bigint,
): bigint[] {
  const elapsed = elapsedSecs > 0n ? elapsedSecs : 0n;
  const retention = fixedPowPpb(snapshot.retentionPpb, elapsed);
  const n = Math.max(snapshot.anchorPpb.length, snapshot.targetPpb.length);
  const projected: bigint[] = [];
  for (let i = 0; i < n; i += 1) {
    projected.push(
      blendConvictionPpb(snapshot.anchorPpb[i] ?? 0n, snapshot.targetPpb[i] ?? 0n, retention),
    );
  }
  return projected;
}

/** Return the leading outcome and total projected conviction. Ties keep the
 * lowest outcome index, matching the contract's first-seen leader scan. */
export function normalizedLeader(projectedPpb: readonly bigint[]): NormalizedLeader | null {
  let leaderPpb = 0n;
  let totalPpb = 0n;
  let outcomeIndex = -1;
  for (let i = 0; i < projectedPpb.length; i += 1) {
    const value = projectedPpb[i] ?? 0n;
    totalPpb += value;
    if (outcomeIndex < 0 || value > leaderPpb) {
      leaderPpb = value;
      outcomeIndex = i;
    }
  }
  if (outcomeIndex < 0 || totalPpb <= 0n || leaderPpb <= 0n) return null;
  return { outcomeIndex, leaderPpb, totalPpb };
}

/** D9-scaled closeness to the normalized resolution threshold:
 * `1e9` means `leader / total == thresholdPpb / 1e9`; values above `1e9`
 * are already past the threshold. */
export function normalizedLeaderScoreD9(
  projectedPpb: readonly bigint[],
  thresholdPpb: bigint,
): bigint {
  if (thresholdPpb <= 0n) return 0n;
  const leader = normalizedLeader(projectedPpb);
  if (leader === null) return 0n;
  return (
    (leader.leaderPpb * CONVICTION_SCALE_PPB * CONVICTION_SCALE_PPB) /
    (thresholdPpb * leader.totalPpb)
  );
}

/** Exact port of the contract's normalized resolution predicate:
 * `leader * 1e9 >= threshold * total`. */
export function normalizedLeaderMeetsThreshold(
  projectedPpb: readonly bigint[],
  thresholdPpb: bigint,
): boolean {
  if (thresholdPpb <= 0n) return false;
  const leader = normalizedLeader(projectedPpb);
  if (leader === null) return false;
  return leader.leaderPpb * CONVICTION_SCALE_PPB >= thresholdPpb * leader.totalPpb;
}

/** Earliest whole chain second when an outcome reaches the normalized
 * resolution threshold. Each outcome's normalized share moves monotonically
 * from its anchor share to its frozen target share, so viable outcomes can be
 * bracketed exponentially and solved with an exact bigint binary search. */
export function projectNormalizedResolution(
  snapshot: ConvictionVectorSnapshot & { readonly updateTsSecs: bigint },
  thresholdPpb: bigint,
): NormalizedResolutionProjection | null {
  if (
    thresholdPpb <= 0n ||
    thresholdPpb > CONVICTION_SCALE_PPB ||
    snapshot.retentionPpb < 0n ||
    snapshot.retentionPpb >= CONVICTION_SCALE_PPB
  ) {
    return null;
  }
  const atAnchor = projectConvictionVectorPpb(snapshot, 0n);
  if (normalizedLeaderMeetsThreshold(atAnchor, thresholdPpb)) {
    return resolutionAt(snapshot.updateTsSecs, atAnchor, thresholdPpb);
  }

  const targetTotal = sumPpb(snapshot.targetPpb);
  if (targetTotal <= 0n) return null;

  let earliestElapsed: bigint | null = null;
  for (let outcomeIndex = 0; outcomeIndex < snapshot.targetPpb.length; outcomeIndex += 1) {
    const targetValue = snapshot.targetPpb[outcomeIndex] ?? 0n;
    if (targetValue * CONVICTION_SCALE_PPB < thresholdPpb * targetTotal) continue;
    const crossing = firstOutcomeCrossing(snapshot, thresholdPpb, outcomeIndex);
    if (crossing !== null && (earliestElapsed === null || crossing < earliestElapsed)) {
      earliestElapsed = crossing;
    }
  }
  if (earliestElapsed === null) return null;

  const projected = projectConvictionVectorPpb(snapshot, earliestElapsed);
  if (!normalizedLeaderMeetsThreshold(projected, thresholdPpb)) return null;
  return resolutionAt(snapshot.updateTsSecs + earliestElapsed, projected, thresholdPpb);
}

function firstOutcomeCrossing(
  snapshot: ConvictionVectorSnapshot,
  thresholdPpb: bigint,
  outcomeIndex: number,
): bigint | null {
  let upper = 1n;
  while (!outcomeMeetsThreshold(snapshot, thresholdPpb, outcomeIndex, upper)) {
    const next = upper * 2n;
    if (next <= upper) return null;
    upper = next;
  }

  let lower = 0n;
  while (lower + 1n < upper) {
    const middle = lower + (upper - lower) / 2n;
    if (outcomeMeetsThreshold(snapshot, thresholdPpb, outcomeIndex, middle)) {
      upper = middle;
    } else {
      lower = middle;
    }
  }
  if (upper > 0n && outcomeMeetsThreshold(snapshot, thresholdPpb, outcomeIndex, upper - 1n)) {
    throw new Error('conviction crossing search violated monotonicity');
  }
  return outcomeMeetsThreshold(snapshot, thresholdPpb, outcomeIndex, upper) ? upper : null;
}

function outcomeMeetsThreshold(
  snapshot: ConvictionVectorSnapshot,
  thresholdPpb: bigint,
  outcomeIndex: number,
  elapsedSecs: bigint,
): boolean {
  const projected = projectConvictionVectorPpb(snapshot, elapsedSecs);
  const outcome = projected[outcomeIndex] ?? 0n;
  const total = sumPpb(projected);
  return total > 0n && outcome * CONVICTION_SCALE_PPB >= thresholdPpb * total;
}

function sumPpb(values: readonly bigint[]): bigint {
  let total = 0n;
  for (const value of values) total += value;
  return total;
}

function resolutionAt(
  resolutionTsSecs: bigint,
  projected: readonly bigint[],
  thresholdPpb: bigint,
): NormalizedResolutionProjection | null {
  const leader = normalizedLeader(projected);
  if (leader === null) return null;
  return {
    outcomeIndex: leader.outcomeIndex,
    resolutionTsSecs,
    normalizedScoreD9: normalizedLeaderScoreD9(projected, thresholdPpb),
  };
}
